import { createPrototypeApiClient } from './apiClient';
import { backFromContextualView, showContextualView, showView } from './navigationController';
import type { PlanCode, PrototypeRuntime, Root } from './prototypeTypes';
import { getRuntime } from './runtimeAccessor';
import { navigateWhenSsrViewMissing } from './ssrDirectNavigation';
import { ensureTopic, getTopic } from './topicStore';
import { getTopicNavigationPreview } from './topicNavigationPreview';
import { paintTopicFallbackFromChapters, paintTopicLoading, paintTopicRecord } from './topicDetailViewPresentation';
import { syncTopicDocumentSeo } from './courseDocumentSeo';
import { canReconcileTopicReturn, markTopicReturnSnapshot } from './topicReturnSnapshot';
import { reconcileTopicReturn } from './topicReturnReconciler';
import { resolveBackToTopic } from './topicArticleHistory';
import { latestChapterProgress } from './chapterProgressTracker';
import { paintTopicPrimaryAction } from './topicDetailPresentation';

/** Coordinates Topic navigation; Topic rendering remains in Topic-only helpers. */
export function showTopicDetail(
	topicId: string,
	root: Root,
	plan: PlanCode = 'free',
	runtime?: PrototypeRuntime,
	options: { origin?: 'browse-card'; initialViewerState?: { isFavorite?: boolean } } = {},
): void {
	if (navigateWhenSsrViewMissing(root, 'topic', `/topics/${encodeURIComponent(topicId)}`, {
		allowBrowseTopicMaterialization: options.origin === 'browse-card',
	})) return;
	const isCurrentNavigation = beginTopicNavigation(root, topicId);
	const currentRuntime = runtime ?? getRuntime(root);
	const target = currentRuntime ?? ({ api: createPrototypeApiClient(), chapterIdByIndex: {} } as PrototypeRuntime);
	syncTopicDocumentSeo(target.api, topicId);
	if (currentRuntime) {
		currentRuntime.currentPlan = plan;
		currentRuntime.activeCourseId = topicId;
		if (!Number.isInteger(currentRuntime.activeChapterIndex) || currentRuntime.activeChapterIndex < 1) currentRuntime.activeChapterIndex = 1;
	}
	const applyFromIndex = (): boolean => {
		if (!isCurrentNavigation()) return false;
		const topic = getTopic(topicId) ?? getTopicNavigationPreview(root, topicId);
		if (!topic) return false;
		if (isReturningFromArticle(root) && canReconcileTopicReturn(root, topic.slug, target)) {
			showContextualView('topic', root);
			const optimisticProgress = latestChapterProgress(target, topic.slug);
			if (optimisticProgress) {
				paintTopicPrimaryAction(root, topic, plan, { loaded: false, response: null }, 1, optimisticProgress);
			}
			void reconcileTopicReturn(root, topic, plan, target);
			return true;
		}
		if (currentRuntime) void currentRuntime.api.recordTopicOpen(topicId).catch(() => undefined);
		markTopicReturnSnapshot(root, topic.slug, target, plan);
		paintTopicRecord(root, topic, plan, target, isCurrentNavigation, options.initialViewerState);
		return true;
	};
	if (applyFromIndex()) {
		showContextualView('topic', root);
		// A Home preview is a first-paint bridge only. Continue loading the
		// authoritative navigation projection in the background so later Article and
		// directory paths retain their store-backed metadata without replacing the
		// already-warm Hero with a cold loading state.
		// An SSR seed is intentionally presentation-only: it may omit inactive
		// chapter rows, so still complete this Topic's navigation projection.
		void ensureTopic(topicId).catch(() => undefined);
		return;
	}
	paintTopicLoading(root, topicId);
	showContextualView('topic', root);
	void ensureTopic(topicId).then(() => {
		if (!isCurrentNavigation()) return;
		if (!applyFromIndex()) void paintTopicFallbackFromChapters(root, topicId, plan, target, isCurrentNavigation);
	}).catch(() => {
		if (isCurrentNavigation()) void paintTopicFallbackFromChapters(root, topicId, plan, target, isCurrentNavigation);
	});
}

function beginTopicNavigation(root: Root, topicId: string): () => boolean {
	const state = root as Root & { __topicNavigation?: { generation: number; topicId: string } };
	const navigation = { generation: (state.__topicNavigation?.generation ?? 0) + 1, topicId };
	state.__topicNavigation = navigation;
	return () => state.__topicNavigation === navigation;
}

function isReturningFromArticle(root: Root): boolean {
	return root.querySelector('#view-article')?.classList.contains('active') ?? false;
}

export function backFromTopic(root: Root): void {
	backFromContextualView(root, { view: 'home', tab: 'home' });
}

export function backToActiveTopic(
	topicId: string,
	root: Root,
	plan: PlanCode = 'free',
	runtime?: PrototypeRuntime,
): void {
	const decision = resolveBackToTopic(window.history.state, topicId, window.history.length);
	if (decision.kind === 'go') {
		window.history.go(decision.delta);
		return;
	}
	const currentState = window.history.state && typeof window.history.state === 'object'
		? { ...window.history.state as Record<string, unknown> }
		: {};
	delete currentState.topicReturnSlug;
	delete currentState.topicReturnDepth;
	window.history.replaceState({ ...currentState, view: 'topic' }, '', decision.path);
	showTopicDetail(topicId, root, plan, runtime);
}
